//
// Copyright (c) 2011-2019 by Curalate, Inc.
//

import React from 'react';
import styled from 'styled-components';
import { ExpandingDataTable } from 'unison';

import { FeedValidationError } from '@apis/catalog-validation/catalogValidationModel';
import { ExpandingDataTableType } from '@catalog-ingestion/models/GoogleCatalogIngestionModalModel';
import { formatErrorLabel } from '@catalog-ingestion/utils/formatErrorLabel';
import { getErrorColumnIndices } from '@catalog-ingestion/utils/getErrorColumnIndices';
import { SecondaryButton } from '@components/atoms/button/Button';
import Icon from '@components/atoms/icon/Icon';
import { SubsectionText } from '@components/atoms/text/Text';

interface Props {
  expandingErrorData: ExpandingDataTableType;
  encodedUri: string;
  productsFailed: number;
}

export const ExpandingDataTables = ({
  expandingErrorData,
  encodedUri,
  productsFailed,
}: Props) => {
  return (
    <>
      {productsFailed > 0 && (
        <>
          <SubHeaderWrapper>
            <StyledSubsectionText>Review Errors</StyledSubsectionText>
            <a href={encodedUri} download='product_catalog_error_data.csv'>
              <SecondaryButton>
                <PanelIcons name='download' />
                Download Errors
              </SecondaryButton>
            </a>
          </SubHeaderWrapper>
          <div>
            {Object.keys(expandingErrorData).map((value) => {
              const errorType = value as FeedValidationError;
              const errorLabel = formatErrorLabel(errorType);
              const errorReason = errorLabel.split(' ')[0];

              // construct element with only the desired data
              const tableDataWithMissing = expandingErrorData[value]?.map(
                (product) => ({
                  rowNumber: product.sample.rowNumber.toString(),
                  feedId: product.sample.feedId || errorReason,
                  title: product.sample.title || errorReason,
                  link: product.sample.link || errorReason,
                  imageLink: product.sample.imageLink || errorReason,
                }),
              );
              if (tableDataWithMissing?.length) {
                return (
                  <ExpandingDataTable
                    key={errorLabel}
                    tableTitle={errorLabel}
                    tableDescription={`These products are just a sample of all of the
                      products that have a ${errorLabel.toLowerCase()}`}
                    customTableHeaders={[
                      'Row',
                      'Id',
                      'Title',
                      'Link',
                      'Image Link',
                    ]}
                    errorColumns={getErrorColumnIndices(errorType)}
                    tableData={tableDataWithMissing}
                  />
                );
              }
            })}
          </div>
        </>
      )}
    </>
  );
};

const SubHeaderWrapper = styled.div`
  display: flex;
  margin-bottom: ${({ theme }) => theme.spacings['2']};
  justify-content: space-between;
`;

const PanelIcons = styled(Icon)`
  color: ${({ theme }) => theme.colors.aliases.primary};
  margin-right: ${({ theme }) => theme.spacings['2']};
  margin-bottom: 2px;

  :hover {
    color: ${({ theme }) => theme.colors.white};
  }
`;

const StyledSubsectionText = styled(SubsectionText)(
  ({ theme }) => `
  padding-top: ${theme.spacings[1]};
`,
);
